import { catchError, useSource, useTranslation } from "@opendash/core";
import { Source } from "@opendash/core/dist/types-generated";
import { ScheduleDataObjectType, scheduleIterator } from "@opendash/schedule";
import { Alert, Flex, Modal, Radio, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { useCallback, useEffect, useState } from "react";
import { Schedule_Template } from "../../../js/parsegenerated";
import { Schedule_Execution } from "../../../js/parsegenerated/Schedule_Execution";
import { OpenService } from "../../../types/config";

export const NextScheduleTimeModal = observer(
  (props: {
    schedule: Schedule_Execution;
    open: boolean;
    nextPlannedExecutionDate: dayjs.Dayjs;
    onOk: (
      values: ScheduleDataObjectType,
      nextIntervalSwitch: number,
      createNewSchedule: boolean
    ) => void;
    onCancel: () => void;
  }) => {
    const t = useTranslation();
    const [source] = useSource();
    const [nextIntervalSwitch, setNextIntervalSwitch] = useState<number>(0);
    const [differenceInExecution, setDifferenceInExecution] =
      useState<number>(0);
    const [originSchedule, setOriginSchedule] = useState<Schedule_Template>();
    const [cronObject, setCronObject] = useState<ScheduleDataObjectType>({
      schedule_type: "timestamp",
      timestamp: undefined,
    });

    const getlastexecution = useCallback(
      async (originSchedule: Schedule_Template) => {
        //Get last execution befor this one
        const [lastExecutionError, lastExecution] = await catchError(
          new Parse.Query(Schedule_Execution)
            .equalTo("origin", originSchedule)
            .equalTo("source", {
              objectId: source.id,
              __type: "Pointer",
              className: Source.className,
            })
            .descending("createdAt")
            .first()
        );

        if (!lastExecutionError) {
          return lastExecution;
        }
      },
      [source]
    );

    useEffect(() => {
      if (
        props.schedule === undefined ||
        props.nextPlannedExecutionDate === undefined
      ) {
        return;
      }

      //Get the origin schedule
      const originSchedule = props.schedule.get("origin");
      if (originSchedule === undefined) {
        return;
      }

      //Parse cron object field using ScheduleCronObjectType
      const cronobj = originSchedule.get("cron") as ScheduleDataObjectType;
      setOriginSchedule(originSchedule);
      if (cronobj !== undefined) {
        setCronObject(cronobj);
      }

      const calculateDifference = async () => {
        const iterator = scheduleIterator(originSchedule.get("cron"));
        //calculate difference between currentcreated and lastcreated
        //Get the createdAt date from last execution
        const lastcreated = (await getlastexecution(originSchedule)).get(
          "createdAt"
        );

        if (iterator.matchesDate(lastcreated)) {
          //Execution is in planned interval
          setDifferenceInExecution(0);
        } else {
          const difference = Math.floor(
            iterator.timeToLastDate(lastcreated, "day")
          );

          setDifferenceInExecution(difference);
        }
      };

      calculateDifference();
    }, [props.schedule, props.nextPlannedExecutionDate]);

    // Check if nextInterval changes and calculate the new cronObject
    useEffect(() => {
      if (originSchedule === undefined || differenceInExecution === 0) {
        return;
      }

      const co = originSchedule.get("cron") as ScheduleDataObjectType;

      let newcronobj = {} as ScheduleDataObjectType;

      // TODO: Add logic to handle cron object creation based on nextIntervalSwitch
      if (nextIntervalSwitch === 0) {
        // newcronobj.run_startdate = co.run_startdate;
      } else if (nextIntervalSwitch === 1) {
        // newcronobj.run_startdate = dayjs(co.run_startdate)
        //   .add(differenceInExecution, "days")
        //   .toDate();
      }
      setCronObject(newcronobj);
    }, [nextIntervalSwitch]);

    return (
      <Modal
        open={props.open}
        onOk={() => {
          props.onOk(
            cronObject,
            nextIntervalSwitch,
            differenceInExecution !== 0
          );
        }}
        onCancel={props.onCancel}
        width={"40%"}
        title={t("maintenance:schedule.execution.nextschedule.title")}
      >
        <Flex gap="middle" align="center" vertical>
          <Typography.Title level={5}>
            {t("maintenance:schedule.execution.nextschedule.description")}
          </Typography.Title>

          <Radio.Group
            onChange={(value) => setNextIntervalSwitch(value.target.value)}
            defaultValue={0}
            style={{ marginBottom: 24 }}
          >
            <Radio.Button value={0}>
              {t(
                "maintenance:schedule.execution.nextschedule.oldinterval.radio.label"
              )}
            </Radio.Button>
            <Radio.Button value={1}>
              {t(
                "maintenance:schedule.execution.nextschedule.newinterval.radio.label"
              )}
            </Radio.Button>
            {/* <Radio.Button value={2}>Individuell</Radio.Button> */}
          </Radio.Group>

          {nextIntervalSwitch === 0 ? (
            <>
              <Typography.Paragraph>
                {t(
                  "maintenance:schedule.execution.nextschedule.oldinterval.description"
                )}
              </Typography.Paragraph>
            </>
          ) : null}
          {nextIntervalSwitch === 1 ? (
            <>
              <Typography.Paragraph>
                {t(
                  "maintenance:schedule.execution.nextschedule.newinterval.description"
                )}
              </Typography.Paragraph>
              {differenceInExecution === 0 ? (
                <Alert
                  type="success"
                  message={t(
                    "maintenance:schedule.execution.nextschedule.newinterval.ininterval"
                  )}
                />
              ) : (
                <Alert
                  type="warning"
                  message={t(
                    "maintenance:schedule.execution.nextschedule.newinterval.shifting.days",
                    { days: differenceInExecution }
                  )}
                />
              )}
            </>
          ) : null}

          {props.nextPlannedExecutionDate ? (
            <Typography.Title level={5}>
              <Tag>
                {t(
                  "maintenance:schedule.execution.nextschedule.nextexecution",
                  {
                    date: props.nextPlannedExecutionDate?.format(
                      OpenService.Formats.date
                    ),
                  }
                )}
              </Tag>
            </Typography.Title>
          ) : null}
        </Flex>
      </Modal>
    );
  }
);
